/**
 * Builds the `q` filter for the identity `/team-members` endpoint used to search
 * agents. Values are interpolated bare (the DSL has no quoting); grammar-
 * significant characters are stripped so free-text input can't malform the query.
 */
import { sanitizeFilterQueryValue } from '@/common/utils/sanitizeFilterQueryValue';

interface AgentTeamScope {
  /** Identity UUID of the current team (the DSL only filters `team.id` by UUID). */
  teamId: string;
  /** `only` restricts to the current team; `exclude` returns every other team. */
  mode: 'only' | 'exclude';
}

interface BuildAgentQueryParams {
  searchValue: string;
  includeReferralAgents: boolean;
  licenseStates?: string[];
  teamScope?: AgentTeamScope;
  includeOnboardingAgents?: boolean;
}

export function buildAgentQuery({
  searchValue,
  includeReferralAgents,
  licenseStates = [],
  teamScope,
  includeOnboardingAgents = false,
}: BuildAgentQueryParams): string {
  const value = sanitizeFilterQueryValue(searchValue);
  const roles = includeReferralAgents ? '[AGENT,REFERRAL_AGENT]' : '[AGENT]';
  const statuses = includeOnboardingAgents
    ? '[ACTIVE,OFFBOARDING,ONBOARDING]'
    : '[ACTIVE,OFFBOARDING]';

  const clauses = [
    `(firstName[match]=${value} | lastName[match]=${value} | displayName[match]=${value})`,
    'user.status[eq]=ACTIVE',
    `roles[in]=${roles}`,
    `status[in]=${statuses}`,
  ];

  if (licenseStates.length) {
    clauses.push(`license.state[in]=[${licenseStates.join(',')}]`);
  }

  if (teamScope) {
    const operator = teamScope.mode === 'only' ? 'eq' : 'ne';
    clauses.push(`team.id[${operator}]=${teamScope.teamId}`);
  }

  return clauses.join(' & ');
}
